import TurnedInIcon from '@mui/icons-material/TurnedIn';
import { Box, DialogTitle, DialogContent, Grid, DialogActions } from '@mui/material';
import { useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import {
  IComposableDashboardCollection,
  IRecruiterTeamNameAutoCompleteOption,
  tagDashboard,
} from 'wisestep-api';
import { WsDialog, WsTypography, WsButton, WsAutoComplete, WsTypoVariants } from 'wisestep-base';

import { IconForDialog } from '../../../../components';
import { WsTeamsFilter } from '../../../../components/filters/components';
import RecruiterSuggestions from '../../../../components/filters/components/RecruiterSuggestions';
import { failed, success } from '../../../../utils/NotificationMessages';
import { useNotificationStore } from '../../../../utils/NotificationStore';
import { defaultRoles } from '../../../accessControl/AddRoleAccessControl';

interface ITagDashboardProps {
  dashboardCollection: IComposableDashboardCollection;
  onCloseDialog: () => void;
  onSave: (profileDashboard: any) => void;
  sharedToTeams: IRecruiterTeamNameAutoCompleteOption[];
  selectedProfileDashboard: any;
}
const TagDashboardDialog = ({
  dashboardCollection,
  onCloseDialog,
  onSave,
  sharedToTeams,
  selectedProfileDashboard,
}: ITagDashboardProps) => {
  const methods = useForm<
    any & {
      sharedToTeams: IRecruiterTeamNameAutoCompleteOption[];
    }
  >({
    defaultValues: {
      ...selectedProfileDashboard,
      sharedToTeams: sharedToTeams,
    },
  });

  const { handleSubmit, setValue } = methods;
  const setNotification = useNotificationStore((state) => state.setNotification);

  useEffect(() => setValue('sharedToTeams', sharedToTeams ?? []), [setValue, sharedToTeams]);

  const onSubmit = async (
    data: any & {
      sharedToTeams: IRecruiterTeamNameAutoCompleteOption[];
    }
  ) => {
    try {
      const updateDashboardState = { ...data };
      updateDashboardState.teams = data.sharedToTeams?.map((team) => team?.id);
      updateDashboardState.dashboardCollection = { ...dashboardCollection, accessLevel: 'shared' };
      const response = await tagDashboard(updateDashboardState);
      onSave(response.data);
      setNotification(success(`Dashbaord ${dashboardCollection?.name} tagged successfully !!`));
    } catch {
      setNotification(failed('Failed to tag dashboard, Please try again !!'));
    }
  };

  return (
    <WsDialog openDialog={true} onClose={onCloseDialog}>
      <FormProvider {...methods}>
        <Box pt={2} component="form" onSubmit={handleSubmit(onSubmit)}>
          <DialogTitle>
            <IconForDialog icon={<TurnedInIcon />} />
          </DialogTitle>
          <DialogContent>
            <Grid mb={1}>
              <WsTypography bold variant={WsTypoVariants.TITLE}>
                {`Tag Dashboard '${dashboardCollection.name}'`}
              </WsTypography>
            </Grid>
            <Box pt={2}>
              <Grid item xs={12} container display="flex" alignItems="center" pt={2}>
                <Grid item xs={2}>
                  <WsTypography>Teams</WsTypography>
                </Grid>
                <Grid xs={10}>
                  <WsTeamsFilter
                    name="sharedToTeams"
                    defaultObjects={sharedToTeams}
                    defaultValue={sharedToTeams?.map((team) => team.name) ?? []}
                  />
                </Grid>
              </Grid>
              <Grid item xs={12} container display="flex" alignItems="center" pt={2}>
                <Grid item xs={2}>
                  <WsTypography>Users</WsTypography>
                </Grid>
                <Grid xs={10}>
                  <RecruiterSuggestions
                    name="users"
                    multiple={true}
                    defaultValue={selectedProfileDashboard?.users}
                  />
                </Grid>
              </Grid>
              <Grid item xs={12} container display="flex" alignItems="center" pt={2}>
                <Grid item xs={2}>
                  <WsTypography>Select Role</WsTypography>
                </Grid>
                <Grid xs={10}>
                  <WsAutoComplete
                    name="roles"
                    optionsInStringArray={defaultRoles?.map((role) => role.label)}
                    multiple={true}
                    initialValue={selectedProfileDashboard?.roles}
                    onTagsChange={(tags: string[]) => methods.setValue('roles', tags)}
                  />
                </Grid>
              </Grid>
            </Box>
          </DialogContent>
          <DialogActions>
            <WsButton onClick={onCloseDialog} variant="outlined">
              Cancel
            </WsButton>
            <WsButton variant="contained" type="submit">
              Save
            </WsButton>
          </DialogActions>
        </Box>
      </FormProvider>
    </WsDialog>
  );
};

export default TagDashboardDialog;
